import { TslhRestoreGate } from './components/TslhRestoreGate';
import { useUserStore } from './lib/userStore';
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { isEmbedDevAllowed } from './lib/devEmbed';
import ScrollToTop from './components/shared/ScrollToTop';
import { AuthPage } from './components/Auth/AuthPage';
import { OidcCallback } from './components/Auth/OidcCallback';
import { OidcAutoLogin } from './components/Auth/OidcAutoLogin';
import { isArsenalMode } from './lib/config';
import { CreateLeague } from './components/CreateLeague';
import { SelectLeague } from './components/League/SelectLeague';
import { JoinLeague } from './components/League/JoinLeague';
import { LeagueDashboard } from './components/Dashboard/LeagueDashboard';
import { MyOffice } from './components/MyOffice/MyOffice';
import { Rules } from './components/Rules/Rules';
import { LeagueRules } from './components/LeagueRules/LeagueRules';
import { FreeAgents } from './components/FreeAgents/FreeAgents';
import { Trade } from './components/Trade/Trade';
import { ActiveAuctions } from './pages/ActiveAuctions';
import { MainLayout } from './components/Layout/MainLayout';
import { MessageCenter } from './components/Messages/MessageCenter';
import { MyTeams } from './components/MyTeams/MyTeams';
import { OtherTeams } from './components/OtherTeams/OtherTeams';
import { Standings } from './components/Standings/Standings';
import { LeagueSettings } from './components/Settings/LeagueSettings';
import { Recommendations } from './components/Recommendations/Recommendations';
import { UpdateUsername } from './components/Settings/UpdateUsername';
import { MyProfile } from './components/MyProfile/MyProfile';
import { DraftPage } from './components/Draft/DraftPage';
import { ProtectionList } from './components/ProtectionList/ProtectionList';
import { LeagueHistory } from './components/LeagueHistory/LeagueHistory';
import { FAQPage } from './components/FAQ/FAQPage';
import { WatchlistPage } from './components/Watchlist/WatchlistPage';
import { AdvancedPlayerSearch } from './components/AdvancedPlayerSearch/AdvancedPlayerSearch';
import { ThemeDemo } from './components/ThemeDemo/ThemeDemo';
import { ThemeTest } from './components/ThemeTest/ThemeTest';
import EmailTest from './components/EmailTest';
import { TutorialPage } from './components/TutorialPage/TutorialPage';
import { UserGuide } from './components/UserGuide/UserGuide';
import TradingBloc from './components/TradingBloc/TradingBloc';
import AppSettings from './components/Admin/AppSettings';
import { LeaguePayment } from './components/Payment/LeaguePayment';
import { PaymentSuccess } from './components/Payment/PaymentSuccess';
import PaiementPage from './components/Paiement/PaiementPage';
import { PlayerDetailPage } from './components/PlayerDetail/PlayerDetailPage';
import { RosterValidation } from './pages/RosterValidation';
import { BuildMyTeam } from './components/BuildMyTeam/BuildMyTeam';
import { LeagueRulesOnboarding } from './components/LeagueRulesOnboarding/LeagueRulesOnboarding';
import { BaseStandardBuildGuard } from './components/LeagueRulesOnboarding/BaseStandardBuildGuard';
import { LineCombinations } from './pages/LineCombinations';
import { NhlEdgeHub } from './pages/NhlEdgeHub';
import { MarketingTools } from './components/MarketingTools/MarketingTools';
import { TeamProfile } from './components/TeamProfile';
import { BpmOnboardingWizard } from './components/BpmOnboarding/BpmOnboardingWizard';
import { EmbedBuildMaintenancePage } from './components/Maintenance/EmbedBuildMaintenancePage';
import { MaintenanceOverlay } from './components/MaintenanceOverlay';
import { CiDashboardApp } from './ci-dashboard/CiDashboardApp';

// DEV-ONLY: lazy-loaded so they are tree-shaken from production builds
const DevEmbedProvider = lazy(() => import('./components/DevEmbed/DevEmbedProvider').then(m => ({ default: m.DevEmbedProvider })));
const BpmEmbedSimulator = lazy(() => import('./components/DevEmbed/BpmEmbedSimulator').then(m => ({ default: m.BpmEmbedSimulator })));
const TslhEmbedEntry = lazy(() => import('./components/DevEmbed/TslhEmbedEntry').then(m => ({ default: m.TslhEmbedEntry })));
import { useThemeStore } from './lib/themeStore';
import { checkVersion, startVersionChecking, stopVersionChecking, forceReload } from './lib/versionCheck';
import { startInactivityMonitoring, stopInactivityMonitoring } from './lib/inactivityCheck';
import { useMaintenanceMode } from './lib/hooks/useMaintenanceMode';
import { MaintenanceGuard } from './components/MaintenanceGuard';
import { AutoLeagueRedirect } from './components/AutoLeagueRedirect';
import { TenantRouteGuard } from './components/TenantRouteGuard';
import { PageViewTracker } from './components/PageViewTracker';
import { TslhSessionGuard } from './components/TslhSessionGuard';
import { BirthdateModal } from './components/BirthdateModal';
import { useLeagueMode } from './lib/hooks/useLeagueMode';
import { useEmbedStore } from './lib/embedStore';
import { useLeagueStore } from './lib/leagueStore';
import { hasAcceptedLeagueRules } from './lib/leagueRulesAcceptance';
import 'bootstrap/dist/css/bootstrap.min.css';
// import LeagueChatBubble from './components/Chat/LeagueChatBubble';

/** Redirige vers /build-my-team si l'équipe n'est pas encore complète (phase de construction standard flexible) */
function BuildPhaseGuard({ children }: { children: ReactNode }) {
  const { isLoading } = useLeagueMode();
  if (isLoading) return null;
  // Ne pas forcer la navigation vers la page de construction initiale.
  // L'utilisateur restera sur la page demandée (ex: /my-office).
  return <>{children}</>;
}

/**
 * Aiguillage de la route `/embed-dev` :
 * - `?tslh=1` → TslhEmbedEntry (active embed + redirige vers /auth pour login TSLH direct)
 * - défaut → DevEmbedProvider (sélecteur DEV pool/user pour BPM)
 */
function EmbedDevRouter() {
  const isTslh = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('tslh') === '1' || params.get('tslh') === 'true';
    } catch {
      return false;
    }
  })();
  return isTslh ? <TslhEmbedEntry /> : <DevEmbedProvider />;
}

/** En mode embed, bloque toute navigation hors du flux de construction tant que l'équipe n'est pas complète */
function EmbedBuildGuard() {
  const isEmbed = useEmbedStore((s) => s.isEmbed);
  const username = useUserStore((s) => s.username);
  const selectedLeague = useLeagueStore((s) => s.selectedLeague);
  const { isBuildPhase, isLoading } = useLeagueMode();
  const { isMaintenance, isLoading: isMaintenanceLoading } = useMaintenanceMode();
  const navigate = useNavigate();
  const location = useLocation();
  // Bypass synchrone : BuildMyTeam dispatche cet event avant que useLeagueMode ait rechargé isBuildPhase
  const [buildJustCompleted, setBuildJustCompleted] = useState(false);

  useEffect(() => {
    const handler = () => setBuildJustCompleted(true);
    window.addEventListener('dynastypool:buildComplete', handler);
    return () => window.removeEventListener('dynastypool:buildComplete', handler);
  }, []);

  useEffect(() => {
    if (!username || !isEmbed || isLoading || isMaintenanceLoading || !selectedLeague?.id) return;

    if (isMaintenance && isBuildPhase) {
      if (location.pathname !== '/maintenance-build') {
        navigate('/maintenance-build', { replace: true });
      }
      return;
    }

    if (!isBuildPhase || buildJustCompleted) return;

    const rulesAccepted = hasAcceptedLeagueRules(selectedLeague.id);
    const path = location.pathname;

    if (!rulesAccepted && path !== '/league-rules-onboarding') {
      navigate('/league-rules-onboarding', { replace: true });
    } else if (
      rulesAccepted &&
      path !== '/build-my-team' &&
      path !== '/league-rules-onboarding' &&
      !path.startsWith('/players/')
    ) {
      navigate('/build-my-team', { replace: true });
    }
  }, [location.pathname, isMaintenance, isMaintenanceLoading, isBuildPhase, isLoading, isEmbed, selectedLeague?.id, username, buildJustCompleted]);

  return null;
}

export default function App() {
  const username = useUserStore((state) => state.username);
  const userId = useUserStore((state) => state.user_id);
  const dateOfBirth = useUserStore((state) => state.date_of_birth);
  const isEmbed = useEmbedStore((s) => s.isEmbed);
  const { theme } = useThemeStore();
  const { isMaintenance, isForced } = useMaintenanceMode();
  const [showBirthdateModal, setShowBirthdateModal] = useState(false);

  // Vérification de version et monitoring
  useEffect(() => {
    const isNewVersion = checkVersion();
    // Sur /auth/bpm et /auth/callback, login()/exchangeCode() font déjà une navigation
    // (redirection OIDC) : un forceReload() concurrent ici peut créer une course et
    // laisser l'utilisateur BPM sur un état incohérent (page blanche). On laisse plutôt
    // ces flux se terminer ; la version sera de toute façon à jour au prochain chargement.
    const isOidcAuthFlow = ['/auth/bpm', '/auth/callback'].some((p) => window.location.pathname.startsWith(p));
    if (isNewVersion && !isOidcAuthFlow) {
      // Recharger directement avec le toast de notification
      forceReload();
    } else {
      // Démarrer la vérification périodique de version seulement si pas de nouvelle version
      startVersionChecking();
    }

    // Démarrer le monitoring d'inactivité si l'utilisateur est connecté
    if (username) {
      startInactivityMonitoring();
    }

    // Cleanup function
    return () => {
      stopVersionChecking();
      stopInactivityMonitoring();
    };
  }, [username, theme]);

  // Surveiller les changements de connexion pour démarrer/arrêter le monitoring
  useEffect(() => {
    if (username) {
      startInactivityMonitoring();
    } else {
      stopInactivityMonitoring();
    }
  }, [username]);

  // Vérifier si l'utilisateur doit fournir sa date de naissance
  useEffect(() => {
    if (username && !dateOfBirth) {
      // L'utilisateur est connecté mais n'a pas de date de naissance
      setShowBirthdateModal(true);
    } else {
      setShowBirthdateModal(false);
    }
  }, [username, dateOfBirth]);

  const handleBirthdateComplete = () => {
    setShowBirthdateModal(false);
  };

  return (
    <>
      <BirthdateModal 
        isOpen={showBirthdateModal} 
        onComplete={handleBirthdateComplete} 
      />
      <MaintenanceOverlay enabled={isForced} forceShow={isForced} />
      <Router>
        <TslhRestoreGate>
        <ScrollToTop />
        {username && <MaintenanceGuard active={isMaintenance} />}
            <AutoLeagueRedirect />
          <TslhSessionGuard />
            <TenantRouteGuard />
            <PageViewTracker />
            <BpmOnboardingWizard />
            <EmbedBuildGuard />
            <Routes>
            {/* DEV-ONLY embed routes — render nothing in production */}
            <Route path="/embed-dev" element={
              isEmbedDevAllowed()
                ? <Suspense fallback={<div className="d-flex justify-content-center mt-5"><div className="spinner-border" /></div>}><EmbedDevRouter /></Suspense>
                : <Navigate to="/auth" replace />
            } />
            <Route path="/dev/bpm-simulator" element={
              isEmbedDevAllowed()
                ? <Suspense fallback={<div className="d-flex justify-content-center mt-5"><div className="spinner-border" /></div>}><BpmEmbedSimulator /></Suspense>
                : <Navigate to="/auth" replace />
            } />
            {/* Community Intelligence Dashboard — section indépendante avec son propre auth et layout.
                Monté hors MainLayout pour éviter tout partage de session/navbar/sidebar avec l'app pool. */}
            <Route path="/ci-dashboard/*" element={<CiDashboardApp />} />
            {/* Mode normal (hors embed) : ne jamais auto-charger /dashboard, seule une navigation explicite y mène */}
            <Route path="/" element={isEmbed ? (username ? <Navigate to="/dashboard" replace /> : <Navigate to="/auth" replace />) : <Navigate to="/auth" replace />} />
            <Route path="/*" element={
              <MainLayout>
                <BaseStandardBuildGuard>
                <Routes>
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/auth/callback" element={isArsenalMode() ? <OidcCallback /> : <Navigate to="/auth" />} />
                  <Route path="/auth/bpm" element={isArsenalMode() ? <OidcAutoLogin /> : <Navigate to="/auth" />} />
                  <Route path="/dashboard" element={username ? <LeagueDashboard /> : <Navigate to="/auth" />} />
                  <Route path="/create-league" element={username ? <CreateLeague /> : <Navigate to="/auth" />} />
                  <Route path="/select-league" element={username ? <SelectLeague /> : <Navigate to="/auth" />} />
                  <Route path="/join-league" element={username ? <JoinLeague /> : <Navigate to="/auth" />} />
                  <Route path="/my-office" element={username ? <MyOffice /> : <Navigate to="/auth" />} />
                  <Route path="/rules" element={username ? <Rules /> : <Navigate to="/auth" />} />
                  <Route path="/league-rules" element={username ? <LeagueRules /> : <Navigate to="/auth" />} />
                  <Route path="/league-rules-onboarding" element={username ? <LeagueRulesOnboarding /> : <Navigate to="/auth" />} />
                  <Route path="/free-agents" element={username ? <BuildPhaseGuard><FreeAgents /></BuildPhaseGuard> : <Navigate to="/auth" />} />
                  <Route
                    path="/advanced-player-search"
                    element={username ? <AdvancedPlayerSearch /> : <Navigate to="/auth" />}
                  />
                  <Route
                    path="/watchlist"
                    element={username ? <WatchlistPage /> : <Navigate to="/auth" />}
                  />
                  <Route path="/my-teams" element={username ? <BuildPhaseGuard><MyTeams /></BuildPhaseGuard> : <Navigate to="/auth" />} />
                  <Route path="/other-teams" element={username ? <OtherTeams /> : <Navigate to="/auth" />} />
                  <Route path="/other-teams/:userId" element={username ? <OtherTeams /> : <Navigate to="/auth" />} />
                  <Route path="/trade-player" element={username ? <Trade /> : <Navigate to="/auth" />} />
                  <Route path="/active-auctions" element={username ? <ActiveAuctions /> : <Navigate to="/auth" />} />
                  <Route path="/trading-bloc" element={username ? <TradingBloc /> : <Navigate to="/auth" />} />
                  <Route path="/messages" element={username ? <MessageCenter /> : <Navigate to="/auth" />} />
                  <Route path="/standings" element={username ? <Standings /> : <Navigate to="/auth" />} />
                  <Route path="/followed-poolers" element={username ? <Standings mode="followed" /> : <Navigate to="/auth" />} />
                  <Route path="/maintenance-build" element={username ? <EmbedBuildMaintenancePage /> : <Navigate to="/auth" />} />
                  <Route path="/tutoriels" element={username ? <TutorialPage /> : <Navigate to="/auth" />} />
                  <Route path="/guide" element={username ? <UserGuide /> : <Navigate to="/auth" />} />
                  <Route path="/settings" element={username ? <LeagueSettings /> : <Navigate to="/auth" />} />
                  <Route path="/recommendations" element={username ? <Recommendations /> : <Navigate to="/auth" />} />
                  <Route path="/update-username" element={username ? <UpdateUsername /> : <Navigate to="/auth" />} />
                  <Route path="/my-profile" element={username ? <MyProfile /> : <Navigate to="/auth" />} />
                  <Route path="/draft" element={username ? <DraftPage /> : <Navigate to="/auth" />} />
                  <Route path="/protection-list" element={username ? <ProtectionList /> : <Navigate to="/auth" />} />
                  <Route path="/league-history" element={username ? <LeagueHistory /> : <Navigate to="/auth" />} />
                  <Route path="/roster-validation" element={username ? <RosterValidation /> : <Navigate to="/auth" />} />
                  <Route path="/build-my-team" element={username ? <BuildMyTeam /> : <Navigate to="/auth" />} />
                  <Route path="/line-combinations" element={username ? <LineCombinations /> : <Navigate to="/auth" />} />
                  <Route path="/nhl-edge-hub" element={username ? <NhlEdgeHub /> : <Navigate to="/auth" />} />
                  <Route
                    path="/marketing-tools"
                    element={username ? (Number(userId) === 3 ? <MarketingTools /> : <Navigate to="/my-office" replace />) : <Navigate to="/auth" />}
                  />
                  <Route
                    path="/admin/app-settings"
                    element={username ? (Number(userId) === 3 ? <AppSettings /> : <Navigate to="/my-office" replace />) : <Navigate to="/auth" />}
                  />
                  <Route path="/faq" element={username ? <FAQPage /> : <Navigate to="/auth" />} />
                  <Route path="/players/:playerId" element={<PlayerDetailPage />} />
                  
                  {/* Routes de paiement */}
                  <Route 
                    path="/league-payment/:leagueId" 
                    element={username ? <LeaguePayment /> : <Navigate to="/auth" />} 
                  />
                  <Route 
                    path="/league-payment-success" 
                    element={username ? <PaymentSuccess /> : <Navigate to="/auth" />} 
                  />
                  {/* Page Paiement DGs */}
                  <Route path="/paiement" element={username ? <PaiementPage /> : <Navigate to="/auth" />} />
                  {/* Page Paiement DGs */}
                  <Route path="/paiement" element={username ? <PaiementPage /> : <Navigate to="/auth" />} />
                  
                  <Route path="/team-profile" element={username ? <TeamProfile /> : <Navigate to="/auth" />} />
                  <Route path="/theme-demo" element={<ThemeDemo />} />
                  <Route path="/theme-test" element={<ThemeTest />} />
                  <Route path="/email-test" element={<EmailTest />} />
                </Routes>
                </BaseStandardBuildGuard>
                {/* Désactivé temporairement pour test iOS WebSocket ("The operation is insecure") */}
                {/* {!isBpmSession && <LeagueChatBubble />} */}
              </MainLayout>
            } />
          </Routes>
      </TslhRestoreGate>
      </Router>
    </>
  );
}
